import React, { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { collection, query, where, orderBy, limit, getDocs, doc, updateDoc, increment } from 'firebase/firestore';
import { X, Copy, ExternalLink, Clock, Tag } from 'lucide-react';

export default function PromoSplashScreen({ isOpen, onClose, userId }) {
  const [promotions, setPromotions] = useState([]);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!isOpen) return;

    const loadPromotions = async () => {
      try {
        setLoading(true);
        const now = new Date();
        
        // Query active promotions that are currently valid
        const promotionsRef = collection(db, 'promotions');
        // Order of filters/orderBy should match composite index
        // Index: isActive (==), priority (desc), validUntil (asc), validFrom (asc)
        const q = query(
          promotionsRef,
          where('isActive', '==', true),
          where('validUntil', '>=', now),
          where('validFrom', '<=', now),
          orderBy('priority', 'desc'),
          orderBy('validUntil', 'asc'),
          orderBy('validFrom', 'asc'),
          limit(5)
        );

        const snapshot = await getDocs(q);
        const promoData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setPromotions(promoData);
        setCurrentPromoIndex(0);
      } catch (error) {
        console.error('Error loading promotions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPromotions();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || promotions.length === 0) return;

    // Auto-advance to next promotion every 5 seconds
    const interval = setInterval(() => {
      setCurrentPromoIndex(prev => 
        prev < promotions.length - 1 ? prev + 1 : 0
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isOpen, promotions.length]);

  // Auto-close after 5 seconds with countdown
  useEffect(() => {
    if (!isOpen || promotions.length === 0) return;

    setCountdown(5);
    
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [isOpen, promotions.length]);

  const handleClose = () => {
    onClose();
    // Mark promotions as viewed for this user
    if (userId && promotions.length > 0) {
      promotions.forEach(promo => {
        updateDoc(doc(db, 'promotions', promo.id), {
          viewCount: increment(1)
        }).catch(console.error);
      });
    }
  };

  const handleClaimPromo = async (promotion) => {
    try {
      // Increment click count
      await updateDoc(doc(db, 'promotions', promotion.id), {
        clickCount: increment(1)
      });

      // Copy coupon code to clipboard
      if (promotion.couponCode) {
        await navigator.clipboard.writeText(promotion.couponCode);
        setCopiedCode(promotion.id);
        setTimeout(() => setCopiedCode(null), 2000);
      }
    } catch (error) {
      console.error('Error claiming promotion:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen || loading) return null;

  if (promotions.length === 0) {
    return null; // Don't show splash if no promotions
  }

  const currentPromo = promotions[currentPromoIndex];

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      style={{
        position: 'fixed',
        top: '0px',
        left: '0px',
        right: '0px',
        bottom: '0px',
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100">
        {/* Countdown Timer */}
        <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium">
          Auto-close in {countdown}s
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Promotion Image */}
        {currentPromo.imageUrl && (
          <div className="relative h-48 bg-gradient-to-br from-blue-500 to-purple-600">
            <img
              src={currentPromo.imageUrl}
              alt={currentPromo.title}
              className="w-full h-full object-cover"
              loading="eager"
              referrerPolicy="no-referrer"
              onError={(e) => {
                // Hide broken external images (e.g., 403 hotlink protection)
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        )}

        {/* Promotion Content */}
        <div className="p-6">
          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {currentPromo.title}
          </h2>

          {/* Description */}
          <p className="text-gray-600 mb-4">
            {currentPromo.description}
          </p>

                     {/* Coupon Code and Discount */}
           {(currentPromo.couponCode || currentPromo.discount) && (
             <div className="bg-gray-50 rounded-lg p-4 mb-4">
               {currentPromo.couponCode && (
                 <>
                   <div className="flex items-center justify-between mb-3">
                     <div className="flex items-center gap-2">
                       <Tag className="w-4 h-4 text-gray-500" />
                       <span className="text-sm text-gray-600">Coupon Code:</span>
                     </div>
                     <button
                       onClick={() => handleClaimPromo(currentPromo)}
                       className="text-sm font-mono font-bold text-blue-600 hover:text-blue-700"
                     >
                       {currentPromo.couponCode}
                     </button>
                   </div>
                   <button
                     onClick={() => handleClaimPromo(currentPromo)}
                     className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                   >
                     {copiedCode === currentPromo.id ? (
                       <>
                         <Copy className="w-4 h-4" />
                         Copied!
                       </>
                     ) : (
                       <>
                         <Copy className="w-4 h-4" />
                         Copy Code
                       </>
                     )}
                   </button>
                 </>
               )}
               
               {currentPromo.discount && (
                 <div className={`${currentPromo.couponCode ? 'mt-3 pt-3 border-t border-gray-200' : ''}`}>
                   <div className="flex items-center gap-2 mb-2">
                     <span className="text-sm text-gray-600">Discount:</span>
                     <span className="text-lg font-bold text-green-600">
                       {currentPromo.discountType === 'percentage' 
                         ? `${currentPromo.discount}% off` 
                         : `₱${currentPromo.discount} off`
                       }
                     </span>
                   </div>
                 </div>
               )}
             </div>
           )}

          {/* Validity Period */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Clock className="w-4 h-4" />
            <span>
              Valid until {formatDate(currentPromo.validUntil)}
            </span>
          </div>

          {/* Action Button */}
          <button
            onClick={() => handleClaimPromo(currentPromo)}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105"
          >
            {currentPromo.actionText || 'Get This Offer'}
          </button>

          {/* Promotion Indicators */}
          {promotions.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {promotions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPromoIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentPromoIndex 
                      ? 'bg-blue-600' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
